import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, CalendarIcon, Clock, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    appointment_time: "",
    notes: "",
  });

  const timeSlots = [
    "9:00 AM",
    "10:00 AM",
    "11:00 AM",
    "1:00 PM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }
    
    if (!formData.appointment_time) {
      toast.error("Please select a time slot");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/appointments`, {
        ...formData,
        appointment_date: format(selectedDate, "yyyy-MM-dd"),
      });
      setSubmitted(true);
      toast.success("Appointment booked successfully!", {
        description: "We'll send you a confirmation email shortly.",
      });
    } catch (error) {
      toast.error("Failed to book appointment", {
        description: "Please try again or call us directly.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="booking-success">
        <Card className="max-w-lg w-full text-center">
          <CardHeader>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold font-['Chivo']">Appointment Confirmed!</CardTitle>
            <CardDescription className="text-base">
              Thank you for booking with 875 Corp
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-sm">
              <p className="font-medium text-slate-900">{formData.name}</p>
              <p className="text-slate-600">{format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
              <p className="text-slate-600">{formData.appointment_time}</p>
              <p className="text-blue-600 font-medium capitalize">{formData.service_type} Consultation</p>
            </div>
            <p className="text-slate-500 text-sm">
              We'll send a confirmation email to {formData.email} with all the details.
            </p>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  name: "",
                  email: "",
                  phone: "",
                  service_type: "",
                  appointment_time: "",
                  notes: "",
                });
                setSelectedDate(undefined);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold"
              data-testid="book-another-btn"
            >
              Book Another Appointment
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="booking-page">
      {/* Hero */}
      <section className="bg-slate-900 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-blue-400 font-bold tracking-wider text-sm">SCHEDULE A VISIT</span>
          <h1 className="text-4xl md:text-6xl font-black text-white mt-4 mb-6 font-['Chivo']">
            Book an Appointment
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Schedule a free consultation with our experts. We'll visit your home, 
            discuss your project, and provide a detailed quote.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Calendar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Chivo']">
                  <CalendarIcon className="w-5 h-5 text-blue-600" />
                  Select a Date
                </CardTitle>
                <CardDescription>Choose your preferred appointment date</CardDescription>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today || date.getDay() === 0;
                  }}
                  className="rounded-sm border"
                  data-testid="booking-calendar"
                />
                
                {selectedDate && (
                  <div className="mt-6">
                    <Label className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-blue-600" />
                      Select a Time
                    </Label>
                    <div className="grid grid-cols-2 gap-2" data-testid="time-slots">
                      {timeSlots.map((time) => (
                        <Button
                          key={time}
                          type="button"
                          variant={formData.appointment_time === time ? "default" : "outline"}
                          className={`rounded-sm ${
                            formData.appointment_time === time 
                              ? "bg-blue-600 text-white" 
                              : "border-slate-200"
                          }`}
                          onClick={() => setFormData(prev => ({ ...prev, appointment_time: time }))}
                          data-testid={`time-slot-${time.replace(/[:\s]/g, '-')}`}
                        >
                          {time}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle className="font-['Chivo']">Your Information</CardTitle>
                <CardDescription>Tell us about yourself and your project</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="h-12"
                      data-testid="booking-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(708) 555-0123"
                      required
                      className="h-12"
                      data-testid="booking-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="h-12"
                    data-testid="booking-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service_type">Service Type *</Label>
                  <Select 
                    value={formData.service_type} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, service_type: value }))}
                    required
                  >
                    <SelectTrigger className="h-12" data-testid="booking-service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="windows">Windows Installation</SelectItem>
                      <SelectItem value="doors">Doors Installation</SelectItem>
                      <SelectItem value="kitchens">Kitchen Remodeling</SelectItem>
                      <SelectItem value="bathrooms">Bathroom Renovation</SelectItem>
                      <SelectItem value="new-construction">New Construction</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Tell us more about your project..."
                    rows={4}
                    data-testid="booking-notes-input"
                  />
                </div>

                {selectedDate && formData.appointment_time && (
                  <div className="bg-blue-50 p-4 rounded-sm border border-blue-100">
                    <p className="font-medium text-blue-900">Selected Appointment:</p>
                    <p className="text-blue-700">
                      {format(selectedDate, "EEEE, MMMM d, yyyy")} at {formData.appointment_time}
                    </p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-sm btn-primary"
                  disabled={loading || !selectedDate || !formData.appointment_time}
                  data-testid="booking-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 spinner" />
                      Booking...
                    </>
                  ) : (
                    "Confirm Appointment"
                  )}
                </Button>
              </CardContent>
            </Card>
          </form>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
